import React, { useEffect, useState } from 'react';
import {
  House,
  ShieldCheck,
  Sparkle,
  Car,
  Plus,
  ArrowRight,
  ArrowSquareOut,
  CaretLeft,
  CaretRight,
  Star,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../ui/carousel';
import FormPrivacyNotice from '../FormPrivacyNotice';
import BookingEnquiryDialog from '../booking/BookingEnquiryDialog';
import { submitLead, normalizePhone } from '@/lib/submitLead';
import { FeatureChipScroller } from './FeatureTiles';

const facilityImages = [
  {
    url: 'https://customer-assets.emergentagent.com/job_petcart-landing/artifacts/jydpeg7i_IMG-20190303-WA0034.jpg',
    title: 'Modern Bamboo Architecture',
    tag: 'Architecture',
    description:
      'Hand-crafted bamboo pavilions designed for natural ventilation, sunlight and a calm environment for your pet.',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_petcart-landing/artifacts/lz3r7pk3_IMG-20190303-WA0031%20%281%29.jpg',
    title: 'Outdoor Play Zone',
    tag: 'Play Zone',
    description:
      'Open, fenced play zones where dogs socialize and burn energy under trained supervision.',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_petcart-landing/artifacts/5ya5ta5z_IMG-20190303-WA0030%20%281%29.jpg',
    title: 'Cottages & Pathways',
    tag: 'Cottages',
    description:
      'Private cottages with climate control, dedicated bedding and individual outdoor access.',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_petcart-landing/artifacts/59in25th_IMG-20190303-WA0029%20%281%29.jpg',
    title: 'Open Grounds',
    tag: 'Grounds',
    description:
      'Two-acre grounds for walks, sprints and supervised group activities throughout the day.',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_petcart-landing/artifacts/konyopv9_IMG-20190303-WA0028%20%281%29.jpg',
    title: 'Resort Exterior',
    tag: 'Exterior',
    description:
      'A resort designed to feel like home — calm exteriors, shaded entries and pet-first wayfinding.',
  },
];

const infoTiles = [
  { icon: House, label: '2 acre campus', sub: 'Open spaces' },
  { icon: ShieldCheck, label: 'Swimming pool', sub: 'Hydrotherapy access' },
  { icon: Sparkle, label: 'Indoor kennels', sub: 'Ventilated rooms' },
  { icon: Car, label: 'Pick zone', sub: 'EV shuttle access' },
];

const ImageCard = ({ image, className = '', onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`landing-bento-media group relative text-left ${className}`}
  >
    <img
      src={image.url}
      alt={image.title}
      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
    <Badge className="absolute left-3 top-4 rounded-md bg-white/95 px-2 py-0.5 text-[10px] font-medium text-neutral-700 shadow-sm hover:bg-white">
      {image.tag}
    </Badge>
    <p className="absolute bottom-3 left-3 right-3 text-xs font-medium leading-tight text-white sm:text-sm">
      {image.title}
    </p>
  </button>
);

const InfoTile = ({ icon: Icon, label, sub }) => (
  <div className="p-4 sm:p-6">
    <Icon size={20} weight="duotone" className="mb-3 text-primary" />
    <p className="landing-card-label">{label}</p>
    <p className="landing-card-meta mt-1">{sub}</p>
  </div>
);

const FacilityImageCarousel = ({ images, onOpen }) => {
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  return (
    <div className="lg:hidden">
      <Carousel
        setApi={setApi}
        opts={{ align: 'start', loop: true }}
        className="w-full"
      >
        <CarouselContent className="-ml-3">
          {images.map((image, i) => (
            <CarouselItem key={image.url} className="basis-[82%] pl-3">
              <ImageCard
                image={image}
                onClick={() => onOpen(i)}
                className="h-[220px] w-full"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="mt-3 flex items-center justify-center gap-1">
        {images.map((image, i) => (
          <button
            key={image.url}
            type="button"
            aria-label={`Show ${image.title}`}
            aria-current={i === selected ? 'true' : undefined}
            onClick={() => api?.scrollTo(i)}
            className="flex h-8 w-8 items-center justify-center"
          >
            <span
              className={`h-1.5 rounded-full transition-all ${i === selected ? 'w-5 bg-primary' : 'w-1.5 bg-neutral-300'}`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const ContactTile = ({ onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group relative flex flex-col items-center justify-center gap-4 overflow-hidden rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-6 text-center transition hover:bg-primary/10 ${className}`}
  >
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white">
      <Plus size={22} weight="bold" />
    </div>
    <p className="landing-card-label">Get in Touch</p>
    <p className="landing-card-meta">See more, ask anything, book a visit</p>
  </button>
);

const TOTAL_STEPS = facilityImages.length + 1; // +1 for contact step

const OurFacility = () => {
  const [step, setStep] = useState(null); // null = closed, 0..TOTAL_STEPS-1 = open
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const openAt = (index) => {
    setConsent(false);
    setStep(index);
  };
  const close = () => {
    setConsent(false);
    setStep(null);
  };
  const prev = () => setStep((s) => (s === null ? null : (s - 1 + TOTAL_STEPS) % TOTAL_STEPS));
  const next = () => setStep((s) => (s === null ? null : (s + 1) % TOTAL_STEPS));

  useEffect(() => {
    if (step === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step]);

  useEffect(() => {
    if (step === null || step === facilityImages.length) return;
    const t = setTimeout(() => {
      setStep((s) => (s === null ? null : (s + 1) % TOTAL_STEPS));
    }, 3500);
    return () => clearTimeout(t);
  }, [step]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    setSubmitting(true);
    const result = await submitLead({
      source: 'facility-tour',
      name: (data.name || '').trim(),
      phone: normalizePhone(data.phone),
      email: (data.email || '').trim(),
      message: ((data.message || '').trim()) || "I'd like to schedule a tour of the campus.",
      consent,
    });
    setSubmitting(false);
    if (result?.success) {
      setConsent(false);
      close();
    }
  };

  const isContactStep = step === facilityImages.length;
  const currentImage = step !== null && !isContactStep ? facilityImages[step] : null;

  return (
    <section id="our-facility" className="bg-neutral-100 pt-10 pb-16 sm:pt-12 sm:pb-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="landing-section-header">
          <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">Our Facility</span>
          <h2 className="landing-section-title">
            A campus designed to feel like
            <br className="hidden sm:block" /> home
          </h2>
          <p className="landing-section-lede mx-auto italic text-sm sm:text-base">Awarded best designed pet resort</p>
        </div>

        <FacilityImageCarousel images={facilityImages} onOpen={openAt} />

        <FeatureChipScroller
          tiles={infoTiles}
          label="Campus features"
          className="mt-4"
        />

        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:mt-0 lg:grid-cols-12 lg:[grid-template-rows:repeat(3,104px)_150px_repeat(4,65px)_80px]">
          <ImageCard
            image={facilityImages[0]}
            onClick={() => openAt(0)}
            className="hidden h-[240px] md:col-span-2 lg:col-span-8 lg:row-span-4 lg:block lg:h-full lg:min-h-[320px]"
          />
          <ImageCard
            image={facilityImages[1]}
            onClick={() => openAt(1)}
            className="hidden h-32 lg:col-span-4 lg:col-start-9 lg:row-start-1 lg:row-span-3 lg:block lg:h-full"
          />
          <ImageCard
            image={facilityImages[2]}
            onClick={() => openAt(2)}
            className="hidden h-32 lg:col-span-4 lg:col-start-9 lg:row-start-4 lg:row-span-1 lg:block lg:h-full"
          />
          <ImageCard
            image={facilityImages[3]}
            onClick={() => openAt(3)}
            className="hidden h-40 lg:col-span-3 lg:col-start-1 lg:row-start-5 lg:row-span-4 lg:block lg:h-[308px] lg:min-h-[308px] lg:self-start"
          />
          <ImageCard
            image={facilityImages[4]}
            onClick={() => openAt(4)}
            className="hidden h-40 lg:col-span-3 lg:col-start-4 lg:row-start-5 lg:row-span-4 lg:block lg:h-[308px] lg:min-h-[308px] lg:self-start"
          />
          <div className="landing-bento-surface hidden overflow-hidden md:col-span-2 lg:col-span-6 lg:col-start-7 lg:row-start-5 lg:row-span-4 lg:grid lg:h-[308px] lg:min-h-[308px] lg:grid-cols-2 lg:self-start">
            {infoTiles.map((tile, idx) => (
              <div
                key={tile.label}
                className={`${idx >= 2 ? 'border-t border-neutral-200' : ''} ${idx % 2 === 1 ? 'border-l border-neutral-200' : ''} flex items-center`}
              >
                <InfoTile {...tile} />
              </div>
            ))}
          </div>

          <a
            href="https://maps.app.goo.gl/6GE5PQzj8i7J6M2aA"
            target="_blank"
            rel="noopener noreferrer"
            className="landing-bento-surface group flex items-center justify-between gap-4 p-4 transition hover:bg-neutral-50 md:col-span-2 lg:col-span-3 lg:col-start-1 lg:row-start-9 lg:h-full"
          >
            <div className="flex items-center gap-4">
              <svg viewBox="0 0 24 24" className="h-10 w-10 shrink-0" aria-hidden="true">
                <path fill="#EA4335" d="M12 2C7.589 2 4 5.589 4 9.995 4 14.5 12 22 12 22s8-7.5 8-12.005C20 5.589 16.411 2 12 2zm0 11a3 3 0 110-6 3 3 0 010 6z"/>
              </svg>
              <div>
                <p className="landing-card-label">PetCart Nest</p>
                <p className="landing-card-meta mt-0.5">Sarjapur, Bangalore</p>
              </div>
            </div>
            <ArrowSquareOut size={18} className="text-neutral-400 group-hover:text-neutral-600" />
          </a>

          <a
            href="https://www.google.com/search?q=petcart+nest+reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="landing-bento-surface group flex items-center justify-between gap-4 p-4 transition hover:bg-neutral-50 md:col-span-2 lg:col-span-3 lg:col-start-4 lg:row-start-9 lg:h-full"
          >
            <div className="flex items-center gap-4">
              <svg viewBox="0 0 48 48" className="h-10 w-10 shrink-0" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="landing-card-label">4.6</p>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={11}
                        weight="fill"
                        className={i < 4 ? 'text-yellow-400' : 'text-yellow-400/60'}
                      />
                    ))}
                  </div>
                </div>
                <p className="landing-card-meta mt-0.5">1,150+ Google reviews</p>
              </div>
            </div>
            <ArrowSquareOut size={18} className="text-neutral-400 group-hover:text-neutral-600" />
          </a>

          <div className="landing-bento-surface flex items-center gap-4 p-4 md:col-span-2 lg:col-span-3 lg:col-start-7 lg:row-start-9 lg:h-full">
            <span className="relative flex h-3 w-3 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
            </span>
            <div>
              <p className="landing-card-meta">Pets hosted</p>
              <p className="text-2xl font-semibold text-neutral-900">4,800+</p>
            </div>
          </div>

          <div className="landing-bento-surface flex items-center justify-between gap-3 bg-primary/5 p-4 md:col-span-2 lg:col-span-3 lg:col-start-10 lg:row-start-9 lg:h-full">
            <div className="min-w-0">
              <p className="landing-card-meta">See it for yourself</p>
              <p className="landing-card-label mt-0.5">Visit our campus</p>
            </div>
            <Button
              type="button"
              onClick={() => setBookingOpen(true)}
              className="h-9 shrink-0 rounded-md bg-primary px-3 text-xs font-semibold text-white hover:bg-primary/90"
            >
              Book
              <ArrowRight size={14} weight="bold" className="ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Unified lightbox carousel — steps 0..N-1 = facility images, last step = contact form */}
      <Dialog open={step !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="!flex h-[72vh] max-h-[680px] w-[95vw] max-w-5xl !gap-0 overflow-hidden rounded-2xl border-none bg-black p-0">
          <DialogTitle className="sr-only">
            {isContactStep ? 'Get in touch' : currentImage?.title}
          </DialogTitle>

          {/* Image step */}
          {currentImage && (
            <div className="relative h-full w-full">
              {facilityImages.map((img, i) => (
                <img
                  key={img.url}
                  src={img.url}
                  alt={img.title}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out ${i === step ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}

              <Badge className="absolute left-5 top-5 z-10 rounded-md bg-white/95 px-2.5 py-1 text-[11px] font-medium text-neutral-700 shadow-sm hover:bg-white">
                {currentImage.tag}
              </Badge>

              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-5 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/70 text-white shadow-lg backdrop-blur-md transition hover:bg-black/85"
              >
                <CaretLeft size={20} weight="bold" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-5 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-black/70 text-white shadow-lg backdrop-blur-md transition hover:bg-black/85"
              >
                <CaretRight size={20} weight="bold" />
              </button>

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent pb-6 pt-20">
                <div className="flex items-end justify-between gap-6 px-6 sm:px-8">
                  <div key={step} className="min-w-0 max-w-xl animate-in fade-in duration-700">
                    <h3 className="text-xl font-semibold text-white sm:text-2xl">
                      {currentImage.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/80">
                      {currentImage.description}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 rounded-full border border-white/20 bg-black/40 px-3 py-1.5 shadow-md backdrop-blur-md">
                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          aria-label={`Go to slide ${i + 1}`}
                          onClick={() => setStep(i)}
                          className={`h-1.5 rounded-full transition-all ${i === step ? 'w-5 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
                        />
                      ))}
                    </div>
                    <span className="text-[11px] font-medium tabular-nums text-white/80">
                      {step + 1} / {TOTAL_STEPS}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contact step */}
          {isContactStep && (
            <div className="grid w-full gap-0 overflow-y-auto bg-white md:grid-cols-[1.4fr_1fr]">
              <div className="relative hidden md:block">
                <img
                  src={facilityImages[0].url}
                  alt="PetCart Nest"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <p className="text-xs font-semibold uppercase text-white/80">PetCart</p>
                  <p className="mt-1 text-lg font-semibold">Visit our Bangalore campus</p>
                </div>
              </div>
              <form onSubmit={handleContactSubmit} className="flex flex-col gap-4 p-6">
                <div>
                  <h3 className="text-left text-xl font-semibold text-neutral-900">Get in touch</h3>
                  <p className="mt-1 text-left text-sm text-neutral-600">
                    Tell us about your pet and we'll set up a tour or callback.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-name" className="text-xs font-semibold">Name</Label>
                  <Input id="contact-name" name="name" required placeholder="Your name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-phone" className="text-xs font-semibold">Phone</Label>
                  <Input id="contact-phone" name="phone" required placeholder="+91 98765 43210" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-email" className="text-xs font-semibold">Email</Label>
                  <Input id="contact-email" name="email" type="email" required placeholder="you@example.com" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-message" className="text-xs font-semibold">Message</Label>
                  <Textarea id="contact-message" name="message" rows={3} maxLength={2000} placeholder="What would you like to know?" />
                </div>
                <FormPrivacyNotice
                  checked={consent}
                  onChange={setConsent}
                  id="facility-privacy-consent"
                />
                <Button type="submit" disabled={submitting} className="mt-2 h-10 rounded-md bg-primary text-sm font-semibold text-white shadow-none hover:bg-primary/90 hover:shadow-none disabled:opacity-60">
                  {submitting ? 'Sending…' : 'Send Enquiry'}
                  <ArrowRight size={14} weight="bold" />
                </Button>
                <p className="text-center text-xs font-medium text-neutral-400">
                  {step + 1} / {TOTAL_STEPS}
                </p>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BookingEnquiryDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </section>
  );
};

export default OurFacility;
